import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { SEO } from '../seo/SEO';

interface CryptoWallet {
  address: string;
  network: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency_code: string;
  currency_symbol: string;
  credits: number;
  duration_days: number;
}

export const CryptoForm: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [selectedWallet, setSelectedWallet] = useState<CryptoWallet | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [transactionId, setTransactionId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedAddress, setCopiedAddress] = useState(false);

  // Crypto wallets
  const wallets: CryptoWallet[] = [
    {
      address: 'TX5niBMWRFjbUnKrb75gA8AJVQKGZ1bXaN',
      network: 'TRC20'
    },
    {
      address: '0x3a4c1bebffdfc7a570303701bc531bd725044818',
      network: 'ERC20/BSC (BEP20)'
    }
  ];

  // Fetch subscription plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase.rpc('get_regional_plans', {
          p_region_code: 'US' // Default to USD pricing
        });

        if (error) throw error;
        setPlans(data || []);
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Failed to load subscription plans. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get current session to verify authentication
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          // Only redirect if we're sure there's no session
          navigate('/signin', { 
            state: { 
              returnTo: '/billing/cryptoform',
              message: 'Please sign in to continue with crypto payment'
            } 
          });
        }
      } catch (err) {
        console.error('Auth check error:', err);
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  // Don't render anything until we confirm authentication
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const handleWalletSelect = (wallet: CryptoWallet) => {
    setSelectedWallet(wallet);
  };

  const handleCopyAddress = async () => {
    if (!selectedWallet) return;
    
    try {
      await navigator.clipboard.writeText(selectedWallet.address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      setError('You must be logged in to submit a transaction');
      return;
    }
    
    if (!selectedWallet) {
      setError('Please select a wallet address');
      return;
    }
    
    if (!selectedPlan) {
      setError('Please select a subscription plan');
      return;
    }
    
    if (!transactionId.trim()) {
      setError('Please enter your transaction ID');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Get plan details
      const plan = plans.find(p => p.id === selectedPlan);
      if (!plan) {
        throw new Error('Selected plan not found');
      }

      // Submit transaction to database
      const { error: submitError } = await supabase
        .from('crypto_transactions')
        .insert({
          transaction_id: transactionId.trim(),
          amount: plan.price,
          user_id: user.id,
          email: user.email, // Keep email for backward compatibility
          currency: 'USDT',
          network: selectedWallet.network,
          wallet_address: selectedWallet.address,
          plan_id: plan.id,
          plan_name: plan.name
        });

      if (submitError) throw submitError;

      // Show success message
      setSuccess(true);
      
      // Reset form
      setSelectedWallet(null);
      setSelectedPlan('');
      setTransactionId('');
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error submitting transaction:', err);
      setError('Failed to submit transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Crypto Payment | Banga.ai"
        description="Complete your subscription payment using cryptocurrency"
      />
      
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Crypto Payment
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Complete your subscription payment using USDT
            </p>
          </div>

          {success ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Transaction Submitted
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Your transaction has been submitted successfully. Our team will verify your payment and activate your subscription shortly.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Redirecting you to the home page...
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Payment Details
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Wallet Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select USDT Wallet
                  </label>
                  <div className="space-y-3">
                    {wallets.map((wallet, index) => (
                      <div 
                        key={index}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedWallet?.address === wallet.address
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        onClick={() => handleWalletSelect(wallet)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              USDT ({wallet.network})
                            </div>
                            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 font-mono break-all">
                              {wallet.address}
                            </div>
                          </div>
                          {selectedWallet?.address === wallet.address && (
                            <button
                              type="button"
                              onClick={handleCopyAddress}
                              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="Copy address"
                            >
                              {copiedAddress ? (
                                <Check className="w-5 h-5" />
                              ) : (
                                <Copy className="w-5 h-5" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Plan Selection */}
                <div>
                  <label htmlFor="plan" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Subscription Plan
                  </label>
                  {isLoading ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : (
                    <select
                      id="plan"
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    >
                      <option value="">Select a plan</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {plan.currency_symbol}{(plan.price / 100).toFixed(2)} ({plan.credits} credits)
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Transaction ID */}
                <div>
                  <label htmlFor="transactionId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Transaction ID
                  </label>
                  <input
                    id="transactionId"
                    type="text"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter your transaction ID"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Enter the transaction ID from your crypto wallet after sending the payment
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Transaction'
                  )}
                </button>

                {/* Instructions */}
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                    How to complete your payment:
                  </h3>
                  <ol className="list-decimal list-inside text-sm text-blue-700 dark:text-blue-200 space-y-2">
                    <li>Select one of the USDT wallet addresses above</li>
                    <li>Send the exact amount for your selected plan to the wallet address</li>
                    <li>Copy the transaction ID from your wallet</li>
                    <li>Paste the transaction ID in the field above and submit</li>
                    <li>Our team will verify your payment and activate your subscription</li>
                  </ol>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};